import { useState, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

/** 최소 표시 시간(ms): 사용자가 "이동 중" UI를 최소한 이 시간만큼 봄 */
const MIN_MS = 1000;
/** 최대 대기 시간(ms): 정리 완료 여부와 무관하게 이 시간이 지나면 반드시 진행 */
const MAX_MS = 3000;

function isIOS() {
  try {
    return Capacitor.getPlatform() === 'ios';
  } catch {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
}

/**
 * PDF 스텝에서 "다음 단계" 전환 시:
 * 1. isTransitioning = true → 오버레이 표시
 * 2. cleanup() 호출 (renderPreview, fullUrl 등 비우기)
 * 3. rAF 2프레임 + iOS 추가 16ms 대기
 * 4. 최소 시간(MIN_MS)과 (3)을 모두 충족하면 send() 호출
 * 5. 최대 시간(MAX_MS) 초과 시 강제 send()
 */
export function usePdfStepTransition({ cleanup, isMountedRef }) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isSentRef = useRef(false);

  const startTransition = useCallback(
    (send) => {
      if (isSentRef.current) return;
      isSentRef.current = true;
      setIsTransitioning(true);

      const startMs = performance.now();

      const doSend = () => {
        if (!isMountedRef.current) return;
        setIsTransitioning(false);
        isSentRef.current = false;
        send();
      };

      // 최대 시간 타이머 (안전망)
      const maxTimer = setTimeout(doSend, MAX_MS);

      const runTeardown = async () => {
        // 1. PDF 상태 비우기
        cleanup();

        // 2. rAF 2프레임 → DOM이 실제로 언마운트될 시간 부여
        await new Promise((r) =>
          requestAnimationFrame(() => requestAnimationFrame(r))
        );

        // 3. iOS 추가 1프레임 + 16ms → GPU 텍스처 반환 여유
        if (isIOS()) {
          await new Promise((r) => requestAnimationFrame(r));
          await new Promise((r) => setTimeout(r, 16));
        }

        // 4. 최소 시간 보장
        const elapsed = performance.now() - startMs;
        if (elapsed < MIN_MS) {
          await new Promise((r) => setTimeout(r, MIN_MS - elapsed));
        }

        clearTimeout(maxTimer);
        doSend();
      };

      runTeardown().catch(() => {
        clearTimeout(maxTimer);
        doSend();
      });
    },
    [cleanup, isMountedRef]
  );

  return { isTransitioning, startTransition };
}
